import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { BottomNav } from "@/components/student/BottomNav";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Zap, Eye, HelpCircle, Plus, Minus, X, Divide, TrendingUp,
  Filter, Brain, Grid3x3, Timer, Swords, Trophy, Loader2, ChevronRight
} from "lucide-react";

const ICONS = { Zap, Eye, HelpCircle, Plus, Minus, X, Divide, TrendingUp, Filter, Brain, Grid3x3, Timer };

const CARD_COLORS = {
  calculation: { bg: "#FFFBEB", border: "#FDE68A", color: "#F59E0B", text: "#B45309" },
  memory: { bg: "#F5F3FF", border: "#DDD6FE", color: "#8B5CF6", text: "#6D28D9" },
  logic: { bg: "#ECFEFF", border: "#A5F3FC", color: "#06B6D4", text: "#0E7490" },
  reaction: { bg: "#FDF2F8", border: "#FBCFE8", color: "#EC4899", text: "#BE185D" },
};

export default function BrainGymPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [modes, setModes] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("brain_modes") || "[]"); } catch { return []; }
  });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([
          axios.get(`${API}/brain-games/modes`, { headers }),
          axios.get(`${API}/brain-games/stats`, { headers }),
        ]);
        setModes(m.data.modes || []);
        try { sessionStorage.setItem("brain_modes", JSON.stringify(m.data.modes || [])); } catch { /* ignore */ }
        setStats(s.data.stats || {});
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-10 font-['Lexend']" data-testid="brain-gym-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/student")} className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="brain-gym-back">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-800 font-['Fredoka'] leading-tight">Brain Gym</h1>
              <p className="text-[11px] text-slate-400 leading-tight">Train your maths brain</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {/* Times Tables Arena feature card */}
        <button
          onClick={() => navigate("/arena")}
          className="w-full mb-5 rounded-2xl p-4 flex items-center gap-4 text-left transition-transform active:scale-[0.99] hover:shadow-md"
          style={{ background: "linear-gradient(135deg,#4F46E5,#7C3AED)" }}
          data-testid="arena-feature-card"
        >
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <Swords className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold font-['Fredoka'] text-lg leading-tight">Times Tables Arena</p>
            <p className="text-white/80 text-xs">Live multiplayer battles · earn coins</p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/80" />
        </button>

        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 px-1">Speed Games</h2>

        {loading && modes.length === 0 ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {modes.map((m) => {
              const Icon = ICONS[m.icon] || Zap;
              const c = CARD_COLORS[m.category] || CARD_COLORS.calculation;
              const best = stats[m.id];
              return (
                <button
                  key={m.id}
                  onClick={() => navigate(`/brain-gym/${m.id}`)}
                  className="rounded-2xl p-4 text-left border-2 transition-transform active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-md flex flex-col"
                  style={{ background: c.bg, borderColor: c.border }}
                  data-testid={`brain-mode-${m.id}`}
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: c.color }}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-bold text-sm leading-tight font-['Fredoka']" style={{ color: c.text }}>{m.name}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex-1">{m.desc}</p>
                  {best ? (
                    <div className="mt-2 flex items-center gap-1 text-[11px] font-semibold" style={{ color: c.text }} data-testid={`brain-best-${m.id}`}>
                      <Trophy className="w-3 h-3" /> Best {best.best_score}
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-slate-400">Not played yet</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
